import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import BackToTop from '@/components/base/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import useSeo from '@/hooks/useSeo';
import useReaddyForm from '@/hooks/useReaddyForm';
import { categories } from '@/mocks/categories';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/dahgcqjp14a1h3mr6lvg';
const HONEYPOT = 'contact_alt';

const principles = [
  {
    icon: 'ri-book-open-line',
    title: '先补齐语境',
    desc: '任何一句被放大的话，都要先还原它出现的场合、时间和上下文，再谈它是否越界。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '两边都摆上桌',
    desc: '我们不隐藏某一方的说法，也不替读者做结论。分歧本身，就是最值得看的部分。',
  },
  {
    icon: 'ri-emotion-normal-line',
    title: '不煽动情绪',
    desc: '标题可以有趣，但不靠制造对立换点击。热闹归热闹，判断权始终留给你。',
  },
  {
    icon: 'ri-refresh-line',
    title: '允许被修正',
    desc: '事件有后续，观点会更新。发现偏差我们会在原文补充说明，而不是悄悄改掉。',
  },
];

const testimonials = [
  {
    name: '林一舟',
    role: '常驻东京 · 市场岗',
    quote:
      '在公司里经常遇到「读空气」的沟通难题，看完那篇职场沟通的整理之后，终于能把憋着的困惑说清楚了。',
  },
  {
    name: 'Alex Chen',
    role: '西雅图 · 产品经理',
    quote:
      '难得有一个中文站点把两边的话都放上来，而不是先替你站好队。看完之后确实会重新想一遍自己的判断。',
  },
  {
    name: '周小满',
    role: '内容从业者',
    quote:
      '道歉那一篇写得挺克制，把「姿态」和「解决方案」拆开讲，是我最近看到最实用的跨文化分析。',
  },
  {
    name: 'K.Mori',
    role: '大阪 · 留学生',
    quote:
      '把网络骂战按平台规则去解释，这个角度很少有人提。看完之后再看评论区，心态平和了很多。',
  },
];

export default function About() {
  const { state, handleSubmit } = useReaddyForm(SUBMIT_ADDR, HONEYPOT);

  useSeo({
    title: '关于我们 - 美日吃瓜频道定位与内容原则 | 美日吃瓜',
    description:
      '了解美日吃瓜的品牌定位、发展理念与内容原则。我们专注整理美日文化冲突案例，呈现多方立场，提供可延伸阅读的语境与联系方式。',
    keywords: '美日吃瓜,关于我们,美日文化冲突,内容原则,跨文化观察',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于美日吃瓜',
        url: 'https://hnqyjk.cn/about',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hnqyjk.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://hnqyjk.cn/about' },
        ],
      },
    ],
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="mt-5 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">关于美日吃瓜</h1>
            <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              美日吃瓜是一个专注围观美日文化冲突的中文内容频道。我们从争议言论、网络骂战、价值观碰撞、政治正确与翻车现场里，挑出真正值得聊的案例，整理成可读、可追溯、可继续讨论的观察笔记。
            </p>
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-10 px-4 py-16 md:px-6 md:py-20 lg:grid-cols-12 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <div className="h-72 w-full overflow-hidden rounded-lg md:h-96">
              <img
                src="https://readdy.ai/api/search-image?query=editorial%20illustration%20of%20an%20open%20notebook%20on%20a%20warm%20cream%20desk%20with%20abstract%20terracotta%20and%20olive%20paper%20scraps%20and%20small%20geometric%20markers%20representing%20cultural%20notes%20flat%20modern%20illustration%20soft%20grain%20no%20text&width=1200&height=800&seq=mr-about-01&orientation=landscape"
                alt="美日吃瓜内容整理工作的插图"
                title="美日吃瓜 - 内容整理与观察"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={100}>
            <SectionHeading eyebrow="Positioning" title="我们想做的事" />
            <div className="space-y-5 text-sm leading-relaxed text-foreground-600 md:text-base">
              <p>
                跨文化的摩擦，很少是非黑即白的对错题。它更像两块拼图拼在一起时发出的声响——尴尬、刺耳，但恰好暴露了双方各自的形状。
              </p>
              <p>
                所以我们不急着下结论。事件的时间线、双方的默认规则、围观者的位置，这些信息被补齐之后，读者自己的判断往往比任何现成结论都更有价值。
              </p>
              <p>
                我们相信「先理解，再评判」。理解不等于认同，而是让你在开口之前，先知道自己到底在反对什么。
              </p>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-4 border-t border-background-200 pt-6">
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">5</p>
                <p className="mt-1 text-xs text-foreground-500">观察栏目</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">128+</p>
                <p className="mt-1 text-xs text-foreground-500">整理话题</p>
              </div>
              <div>
                <p className="font-heading text-2xl font-bold text-primary-600">36 万</p>
                <p className="mt-1 text-xs text-foreground-500">月阅读人次</p>
              </div>
            </div>
          </Reveal>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
            <SectionHeading
              eyebrow="Principles"
              title="内容原则"
              desc="这四条规则决定了我们怎么选题、怎么写作，也决定了哪些热闹我们不会去蹭。"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((item, index) => (
                <Reveal key={item.title} delay={index * 70}>
                  <div className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <SectionHeading
            eyebrow="Columns"
            title="频道栏目"
            desc="五个栏目各自对应一类摩擦，你可以从最感兴趣的那一类开始。"
          />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {categories.map((cat, index) => (
              <Reveal key={cat.slug} delay={index * 60}>
                <div className="flex h-full items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${cat.icon} text-xl`}></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-950">{cat.name}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{cat.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
            <SectionHeading eyebrow="Readers" title="读者怎么说" desc="来自不同城市的读者，把他们看到这套内容之后的变化留了下来。" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {testimonials.map((item, index) => (
                <Reveal key={item.name} delay={index * 70}>
                  <figure className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-6">
                    <i className="ri-double-quotes-l text-2xl text-accent-500"></i>
                    <blockquote className="mt-4 flex-1 text-sm leading-relaxed text-foreground-700">
                      {item.quote}
                    </blockquote>
                    <figcaption className="mt-5 flex items-center gap-3 border-t border-background-200 pt-4">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-user-smile-line"></i>
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground-950">{item.name}</span>
                        <span className="block text-xs text-foreground-500">{item.role}</span>
                      </span>
                    </figcaption>
                  </figure>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
            <div className="lg:col-span-5">
              <SectionHeading eyebrow="Contact" title="联系我们" desc="投稿、纠错、合作或只是想聊聊，都可以从这里找到我们。" />
              <ul className="space-y-5">
                <li className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">邮件联系与投稿</p>
                    <a
                      href="mailto:contact@meirichigua.com"
                      className="mt-1 block text-sm text-primary-600 underline-offset-4 hover:underline"
                    >
                      contact@meirichigua.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                    <i className="ri-time-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">回复时间</p>
                    <p className="mt-1 text-sm text-foreground-600">通常在两个工作日内回复，投稿类会稍慢一些。</p>
                  </div>
                </li>
                <li className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                    <i className="ri-file-text-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">投稿小建议</p>
                    <p className="mt-1 text-sm text-foreground-600">
                      附上原始链接与时间线，说明你想讨论的那个「文化摩擦点」，会更容易被采用。
                    </p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="lg:col-span-7">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
                <h3 className="font-heading text-xl font-bold text-foreground-950">给我们留个话</h3>
                <p className="mt-2 text-sm text-foreground-600">
                  填写下面的表单，我们会通过邮件回复你。带星号的为必填项。
                </p>

                <form
                  id="meiri-contact-form"
                  data-readdy-form
                  action={SUBMIT_ADDR}
                  method="post"
                  onSubmit={handleSubmit}
                  className="mt-6"
                >
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="contact-name" className="mb-2 block text-sm font-medium text-foreground-800">
                        称呼 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        placeholder="怎么称呼你"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="mb-2 block text-sm font-medium text-foreground-800">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        placeholder="you@example.com"
                        className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-topic" className="mb-2 block text-sm font-medium text-foreground-800">
                        事项类型
                      </label>
                      <select
                        id="contact-topic"
                        name="topic"
                        defaultValue="投稿"
                        className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
                      >
                        <option value="投稿">话题投稿</option>
                        <option value="纠错">内容纠错</option>
                        <option value="转载">转载与引用</option>
                        <option value="合作">合作沟通</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="contact-message" className="mb-2 block text-sm font-medium text-foreground-800">
                        想说的话 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={5}
                        required
                        maxLength={500}
                        placeholder="简单介绍事件背景、你观察到的摩擦点，以及希望我们关注的角度。"
                        className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      ></textarea>
                    </div>
                  </div>

                  <input
                    className="form-honeypot"
                    type="text"
                    name={HONEYPOT}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="mt-6 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                    <button
                      type="submit"
                      disabled={state.status === 'submitting'}
                      className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    >
                      {state.status === 'submitting' ? (
                        <>
                          <i className="ri-loader-4-line animate-spin"></i>
                          提交中
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line"></i>
                          提交信息
                        </>
                      )}
                    </button>
                    <p className="text-xs leading-relaxed text-foreground-500">
                      我们仅将你的联系方式用于回复本次沟通。
                    </p>
                  </div>

                  {state.status === 'success' && (
                    <p className="mt-4 flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-800">
                      <i className="ri-checkbox-circle-line"></i>
                      {state.message}
                    </p>
                  )}
                  {state.status === 'error' && (
                    <p className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
                      <i className="ri-error-warning-line"></i>
                      {state.message}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}